// Calendar view of the core (R-E10): any type with a date — a month of weeks from Monday, a record over several days one bar across them,
// the hour of a text field before the title, the colour of the view's first label column; «ещё N» opens the day; on a phone — the days
// with records as a list. The same query as the table: its filter, the chosen event and the rights.

import { useMemo, useState } from 'react';
import { accountToday, recordTitle, type FieldDef, type Id, type RecordObj, type ViewDef } from '@confdeck/core-shared';
import { useMeta, useSync, useTypesTick } from '../sync/react.tsx';
import { Button, cx, IconButton, Popover, PopoverContent, PopoverTrigger, useNarrow } from '../ui/primitives.tsx';
import { addDay, itemOf, layMonth, monthLabel, monthOf, type CalItem } from './month.ts';
import s from './calendar.module.css';

const LANES = 3;
const WEEK = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

type Entry = { id: Id; title: string; time: string; color: string };

export function Calendar({ typeId, view, ids, onOpen }: { typeId: Id; view: ViewDef; ids: Id[]; onOpen: (id: Id, e?: React.MouseEvent) => void }) {
  const meta = useMeta();
  const client = useSync();
  useTypesTick([typeId]);
  const narrow = useNarrow(640);
  const today = accountToday(meta);
  const [first, setFirst] = useState(() => monthOf(today));
  const c = view.calendar;
  // The colour of a record: the term of the view's first label column (a status), as on the board.
  const colorField = view.columns.filter((x) => !x.hidden).map((x) => meta.field(typeId, x.field)).find((f): f is FieldDef => !!f && f.fieldType === 'taxonomy' && !f.options.multiple);

  const { items, entries, undated } = useMemo(() => {
    const items: CalItem[] = [];
    const entries = new Map<Id, Entry>();
    const undated: Id[] = [];
    for (const id of ids) {
      const r = client.store.record(id);
      if (!r || !c) continue;
      entries.set(id, entryOf(r));
      const it = itemOf(id, r.data[c.start], c.end ? r.data[c.end] : null);
      if (it) items.push(it); else undated.push(id);
    }
    return { items, entries, undated };
    function entryOf(r: RecordObj): Entry {
      const t = colorField ? meta.term(String(r.data[colorField.key] ?? '')) : undefined;
      const time = c?.time && typeof r.data[c.time] === 'string' ? String(r.data[c.time]) : '';
      return { id: r.id, title: recordTitle(r, meta), time, color: t?.color ?? 'blue' };
    }
  }, [ids, c?.start, c?.end, c?.time, colorField?.key, client.store, meta]);
  const weeks = useMemo(() => layMonth(first, items, LANES), [first, items]);
  if (!c) return <div className={s.empty}>У вида не выбрано поле даты: «Вид → Раскладка → Календарь».</div>;

  const month = first.slice(0, 7);
  const onDay = (day: string) => items.filter((it) => it.start <= day && it.end >= day).map((it) => entries.get(it.id)!).sort(byTime);
  const chip = (e: Entry, extra?: string) => (
    <button key={e.id} type="button" className={cx(s.chip, extra)} style={tone(e.color)} title={e.time ? `${e.time} ${e.title}` : e.title} onClick={(ev) => onOpen(e.id, ev)}>
      {e.time && <span className={s.time}>{e.time}</span>}<span className={s.title}>{e.title}</span>
    </button>
  );
  const head = (
    <div className={s.head}>
      <div className={s.nav} role="group" aria-label="Месяц">
        <IconButton icon="chevronLeft" label="Прошлый месяц" onClick={() => setFirst(monthOf(first, -1))} />
        <h2 className={s.month} aria-live="polite">{monthLabel(first)}</h2>
        <IconButton icon="chevronRight" label="Следующий месяц" onClick={() => setFirst(monthOf(first, 1))} />
      </div>
      <Button size="sm" variant="secondary" onClick={() => setFirst(monthOf(today))} disabled={first === monthOf(today)}>Сегодня</Button>
    </div>
  );
  const rest = undated.length > 0 && (
    <div className={s.undated}><span className={s.undatedLabel}>Без даты · {undated.length}</span>{undated.slice(0, 50).map((id) => chip(entries.get(id)!))}</div>
  );

  if (narrow) {
    // A phone: the days of the month that have records, as a list.
    const days: string[] = [];
    for (let d = first; d.startsWith(month); d = addDay(d, 1)) if (items.some((it) => it.start <= d && it.end >= d)) days.push(d);
    return (
      <div className={s.calendar} data-calendar>
        {head}
        {days.length === 0 && <div className={s.empty}>В этом месяце ничего нет.</div>}
        <ol className={s.agenda}>
          {days.map((d) => (
            <li key={d} className={cx(s.agendaDay, d === today && s.isToday)}>
              <span className={s.agendaDate}>{new Date(`${d}T00:00:00Z`).toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'long', timeZone: 'UTC' })}</span>
              <div className={s.agendaItems}>{onDay(d).map((e) => chip(e))}</div>
            </li>
          ))}
        </ol>
        {rest}
      </div>
    );
  }
  return (
    <div className={s.calendar} data-calendar>
      {head}
      <div className={s.weekdays} aria-hidden="true">{WEEK.map((w) => <span key={w}>{w}</span>)}</div>
      <div className={s.weeks} role="grid" aria-label={monthLabel(first)}>
        {weeks.map((w) => (
          <div key={w.days[0]} className={s.week} role="row">
            {w.days.map((d, col) => (
              <div key={d} role="gridcell" className={cx(s.day, !d.startsWith(month) && s.outside, d === today && s.isToday)} style={{ gridColumn: col + 1 }} aria-label={d}>
                <span className={s.num}>{Number(d.slice(8))}</span>
              </div>
            ))}
            {w.bars.map((b) => {
              const e = entries.get(b.id)!;
              return (
                <div key={b.id} className={s.bar} style={{ gridColumn: `${b.from + 1} / ${b.to + 2}`, gridRow: b.lane + 2 }}>
                  {chip(e, cx(b.before && s.cutStart, b.after && s.cutEnd))}
                </div>
              );
            })}
            {w.more.map((list, col) => list.length > 0 && (
              <div key={`more-${col}`} className={s.moreCell} style={{ gridColumn: col + 1, gridRow: LANES + 2 }}>
                <Popover>
                  <PopoverTrigger asChild><button type="button" className={s.more}>ещё {list.length}</button></PopoverTrigger>
                  <PopoverContent width={280}>
                    <div className={s.dayList}>
                      <b>{new Date(`${w.days[col]}T00:00:00Z`).toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })}</b>
                      {/* The list is what «ещё N» counts: the records of the day that found no lane, not the ones already drawn as bars. */}
                      {list.map((id) => entries.get(id)!).sort(byTime).map((x) => chip(x))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            ))}
          </div>
        ))}
      </div>
      {rest}
    </div>
  );
}

/** The colours of a label (tokens c-<colour>-bg/fg/dot), as the chips of the core take them. */
const tone = (color: string) => ({ '--cal-bg': `var(--c-${color}-bg)`, '--cal-fg': `var(--c-${color}-fg)`, '--cal-dot': `var(--c-${color}-dot)` }) as React.CSSProperties;
const byTime = (a: Entry, b: Entry) => (a.time || '99').localeCompare(b.time || '99') || a.title.localeCompare(b.title, 'ru');
